import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useRef, useEffect } from 'react';
import AddAdminUser from '../components/AddAdminUser';
import DataTable from 'datatables.net-dt';
import 'datatables.net-dt/css/dataTables.dataTables.css';
import $ from 'jquery';
import { getUsers, updateUser, createUser, deleteUser, getTemporaryPassword } from '../lib/api';
import { DecryptFE, EncryptFE } from '../lib/encrypt';
import { toast } from 'react-toastify';

const customStyles = `
  .dataTables_wrapper {
    padding: 0 !important;
    border: none !important;
    background: none !important;
  }
  .dataTables_wrapper .dataTables_filter,
  .dataTables_wrapper .dataTables_paginate,
  .dataTables_wrapper .dataTables_info,
  .dataTables_wrapper .dataTables_length {
    display: none !important;
  }
  .tooltip-menu {
    min-width: 140px;
    padding: 0.5rem;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    background: #fff;
    z-index: 1000;
  }
  .tooltip-item {
    padding: 0.5rem 1rem;
    cursor: pointer;
    font-size: 0.875rem;
    color: #4a4a4a;
  }
  .tooltip-item:hover {
    background-color: #f1f5f9;
    color: #1a73e8;
  }
  .form-select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' fill='currentColor' class='bi bi-chevron-down' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 8px center;
    background-size: 14px;
    padding-right: 20px;
    padding-left: 8px;
    height: 32px;
    line-height: 22px;
    min-width: 70px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    color: #333;
  }
`;

const AdminUsersList = () => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userToEdit, setUserToEdit] = useState(null);
    const [showEntries, setShowEntries] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [apiLoading, setApiLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);
    const searchInputRef = useRef(null);
    const [deleteLoadingId, setDeleteLoadingId] = useState(null);
    const loggedInUser = JSON.parse(localStorage.getItem("user"));
    // Map API data to component format
    const mapApiDataToUser = (apiUser) => {
        return {
            id: apiUser.ID,
            name: apiUser.Name,
            surname: apiUser.Surname,
            email: DecryptFE(apiUser.EmailID),
            phone: DecryptFE(apiUser.PhoneNumber),
            address: DecryptFE(apiUser.Address),
            status: apiUser.IsActive ? 'Active' : 'Inactive',
            permissions: {
                userManagement: apiUser.IsUserManagementAllowed,
                cropManagement: apiUser.IsCropManagementAllowed,
                reportAccess: apiUser.IsReportAccessAllowed,
                deleteAllowed: apiUser.IsDeleteAllowed
            },
            password: apiUser.IsPasswordAutoGenerated ? 'AutoGenerated' : 'Manual',
            isActive: apiUser.IsActive,
            isPasswordAutoGenerated: apiUser.IsPasswordAutoGenerated,
            createdBy: apiUser.CreatedBy,
            createdOn: apiUser.CreatedOn,
            modifiedBy: apiUser.ModifiedBy,
            modifiedOn: apiUser.ModifiedOn
        };
    };

    // Fetch users from API
    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getUsers();

            let usersData = response;

            if (response && response.users) {
                usersData = response.users;
            }

            if (Array.isArray(usersData)) {
                const mappedUsers = usersData.map(mapApiDataToUser);
                setUsers(mappedUsers);
            } else {
                console.error('Unexpected API response format:', response);
                setError('Invalid data format received from server.');
            }
        } catch (err) {
            setError('Failed to fetch users. Please try again.');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchUsers();
    }, []);

    const toggleDrawer = (user = null) => {
        setUserToEdit(user);
        setIsDrawerOpen(!isDrawerOpen);
        setSuccessMessage(''); // Clear success message when opening/closing drawer
    };

    const handleEditUser = (userId) => {
        const user = users.find(u => u.id === userId);
        if (user) {
            toggleDrawer(user);
        }
    };
    const handleDeleteUser = async (userId) => {
        const user = users.find(u => u.id === userId);
        const loggedInUser = JSON.parse(localStorage.getItem("user"));

        if (loggedInUser && user && loggedInUser.EmailID === user.email) {
            alert("You cannot delete your own account.");
            return;
        }

        if (!window.confirm("Are you sure you want to delete this user?")) return;

        try {
            setDeleteLoadingId(userId);
            setError(null);

            const token = localStorage.getItem("token");
            await deleteUser(userId, token);

            setSuccessMessage("User deleted successfully!");

            await fetchUsers();
        } catch (err) {
            console.error("Delete error:", err);
            setError("Failed to delete user. Try again.");
        } finally {
            setDeleteLoadingId(null);
        }
    };


    const handleMenuClick = (userId, event) => {
        const button = event.currentTarget;
        const tooltip = button.nextElementSibling;
        tooltip.style.display = tooltip.style.display === 'block' ? 'none' : 'block';

        const closeTooltip = (event) => {
            if (!button.contains(event.target) && !tooltip.contains(event.target)) {
                tooltip.style.display = 'none';
                document.removeEventListener('click', closeTooltip);
            }
        };

        document.addEventListener('click', closeTooltip);
    };

    // Handle form submission for both create and update
    const handleSubmit = async (formData, isUpdate) => {
        try {
            setApiLoading(true);
            setError(null);
            setSuccessMessage('');
            let response;

            if (isUpdate) {
                // Update existing user
                response = await updateUser(formData);
                toast('User updated successfully!');
            } else {
                // Create new user
                console.log("form", formData);
                response = await createUser(formData);
                toast('User created successfully!');
            }

            // Handle API response
            if (response && response.users) {
                // Update local state with the response from API
                const updatedUsers = response.users.map(mapApiDataToUser);
                setUsers(updatedUsers);
            } else {
                // If API doesn't return users array, refetch all users
                await fetchUsers();
            }

            // Close drawer after successful operation
            setTimeout(() => {
                setIsDrawerOpen(false);
                setUserToEdit(null);
            }, 1500);

        } catch (err) {
            console.error('Error submitting user data:', err);
            setError(err.message || `Failed to ${isUpdate ? 'update' : 'create'} user. Please try again.`);
        } finally {
            setApiLoading(false);
        }
    };

    const handleEmailCredentials = (email, password) => {
        alert(`Emailing credentials to ${email}: Password is ${password}`);
    };

    const handleShowCredentials = async (userId) => {
        try {
            const response = await getTemporaryPassword(userId);
            console.log("temp password response", response);
            if (!response || !response.temporaryPassword) {
                alert("No temporary password found for this user.");
                return;
            }

            alert(`Temporary Password: ${response.temporaryPassword}`);
        } catch (err) {
            console.error("Error fetching temporary password:", err);
            alert("Failed to fetch credentials. Try again.");
        }
    };

    const initializeDataTable = () => {
        if (tableRef.current && !dataTableRef.current && users.length > 0) {
            dataTableRef.current = new DataTable(tableRef.current, {
                paging: true,
                pageLength: showEntries,
                lengthChange: false,
                searching: true,
                ordering: true,
                info: false,
                dom: 't',
                columnDefs: [
                    { targets: 2, width: '150px' },
                    {
                        targets: 6,
                        orderable: false,
                        searchable: false,
                        width: '120px',
                        createdCell: (td, cellData, rowData, row, col) => {
                            const userId = users[row]?.id;
                            if (userId) {
                                const editBtn = td.querySelector('.edit-btn');
                                const menuBtn = td.querySelector('.menu-btn');
                                const deleteBtn = td.querySelector('.delete-btn');

                                if (editBtn) {
                                    editBtn.onclick = () => handleEditUser(userId);
                                }
                                if (menuBtn) {
                                    menuBtn.onclick = (e) => handleMenuClick(userId, e);
                                }
                                if (deleteBtn) {
                                    deleteBtn.onclick = () => handleDeleteUser(userId);
                                }
                            }
                        }
                    },
                ],
                order: [[0, 'asc']],
                drawCallback: function () {
                    const pageInfo = this.api().page.info();
                    setCurrentPage(pageInfo.page + 1);
                    const deleteButtons = tableRef.current?.querySelectorAll('.delete-btn');
                    deleteButtons?.forEach(btn => {
                        const userId = btn.getAttribute('data-user-id');
                        btn.onclick = () => handleDeleteUser(userId);
                    });


                    setTimeout(() => {
                        const editButtons = tableRef.current?.querySelectorAll('.edit-btn');
                        const menuButtons = tableRef.current?.querySelectorAll('.menu-btn');

                        editButtons?.forEach(btn => {
                            const userId = btn.getAttribute('data-user-id');
                            btn.onclick = () => handleEditUser(userId);
                        });

                        menuButtons?.forEach(btn => {
                            const userId = btn.getAttribute('data-user-id');
                            btn.onclick = (e) => handleMenuClick(userId, e);
                        });
                    }, 0);
                },
            });

            if (searchInputRef.current) {
                searchInputRef.current.addEventListener('input', e => {
                    if (dataTableRef.current) dataTableRef.current.search(e.target.value).draw();
                });
            }
        }
    };

    const handlePreviousPage = () => {
        if (dataTableRef.current && currentPage > 1) {
            dataTableRef.current.page('previous').draw('page');
        }
    };

    const handleNextPage = () => {
        if (dataTableRef.current && currentPage * showEntries < users.length) {
            dataTableRef.current.page('next').draw('page');
        }
    };

    const handlePageChange = page => {
        if (dataTableRef.current) dataTableRef.current.page(page - 1).draw('page');
    };

    useEffect(() => {
        window.jQuery = window.$ = $;
        return () => {
            if (dataTableRef.current) {
                dataTableRef.current.destroy();
                dataTableRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (dataTableRef.current) {
            dataTableRef.current.page.len(showEntries).draw();
        }
    }, [showEntries]);

    useEffect(() => {
        if (users.length > 0) {
            if (dataTableRef.current) {
                dataTableRef.current.destroy();
                dataTableRef.current = null;
            }
            setTimeout(() => initializeDataTable(), 100);
        }
    }, [users]);
    console.log("users", users);
    // Show loading state
    if (loading) {
        return (
            <div className="card h-100 p-24 radius-12 d-flex align-items-center justify-content-center">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-secondary">Loading users...</p>
                </div>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className="card h-100 p-24 radius-12 d-flex align-items-center justify-content-center">
                <div className="text-center">
                    <Icon icon="material-symbols:error-outline" className="text-danger" style={{ fontSize: '48px' }} />
                    <p className="mt-2 text-danger">{error}</p>
                    <button
                        className="btn btn-primary mt-3"
                        onClick={fetchUsers}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{customStyles}</style>

            {/* Success Message */}
            {successMessage && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                    {successMessage}
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setSuccessMessage('')}
                    ></button>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setError(null)}
                    ></button>
                </div>
            )}

            <div className="card h-100 p-0 radius-12">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <div className="d-flex align-items-center flex-nowrap gap-2">
                        <span className="text-sm text-secondary-light">Show</span>
                        <select
                            className="form-select form-select-sm btn btn-outline-secondary btn-sm dropdown-toggle py-2 px-3 radius-6"
                            value={showEntries}
                            onChange={e => setShowEntries(parseInt(e.target.value))}
                            disabled={apiLoading}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={-1}>All</option>
                        </select>

                        <div className="navbar-search">
                            <div className="relative">
                                <input
                                    type="text"
                                    name="search"
                                    placeholder="Search"
                                    ref={searchInputRef}
                                    className="form-control h-[36px] w-[54.25rem] pl-10 pr-3 text-sm border border-neutral-300 radius-6 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                                    disabled={apiLoading}
                                />
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => toggleDrawer()}
                        className="btn btn-yellow text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                        disabled={apiLoading}
                    >
                        <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                        {apiLoading ? 'Processing...' : 'Add New User'}
                    </button>
                </div>

                    {users.length === 0 ? (
                        <div className="text-center py-24">
                            <Icon icon="material-symbols:group-off" className="text-neutral-400" style={{ fontSize: '64px' }} />
                            <p className="mt-3 text-neutral-600">No users found</p>
                            <button
                                className="btn btn-primary mt-3"
                                onClick={() => toggleDrawer()}
                                disabled={apiLoading}
                            >
                                Add First User
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive scroll-sm p-16">
                                <table ref={tableRef} className="table bordered-table sm-table mb-0" id="adminUsersTable">
                                    <thead>
                                        <tr>
                                            <th scope="col">Name</th>
                                            <th scope="col">Surname</th>
                                            <th scope="col">Email</th>
                                            <th scope="col">Phone</th>
                                            <th scope="col">Address</th>
                                            <th scope="col" className="text-center">Status</th>
                                            <th scope="col" className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map(user => (
                                            <tr key={user.id}>
                                                <td><span className="text-md mb-0 fw-normal text-secondary-light">{user.name}</span></td>
                                                <td><span className="text-md mb-0 fw-normal text-secondary-light">{user.surname}</span></td>
                                                <td><span className="text-md mb-0 fw-normal text-secondary-light">{user.email}</span></td>
                                                <td><span className="text-md mb-0 fw-normal text-secondary-light">{user.phone}</span></td>
                                                <td>
                                                    <span
                                                        className="text-md mb-0 fw-normal text-secondary-light d-inline-block text-wrap"
                                                        style={{ maxWidth: '180px' }}
                                                    >
                                                        {user.address}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`px-24 py-4 radius-4 fw-medium text-sm ${user.status === 'Active'
                                                        ? 'bg-success-focus text-success-600 border border-success-main'
                                                        : 'bg-neutral-200 text-neutral-600 border border-neutral-400'
                                                        }`}>{user.status}</span>
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex align-items-center gap-10 justify-content-center position-relative">
                                                        <button
                                                            type="button"
                                                            className="edit-btn bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            data-user-id={user.id}
                                                            disabled={apiLoading}
                                                        >
                                                            <Icon icon="lucide:edit" className="menu-icon" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="delete-btn bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            data-user-id={user.id}
                                                            disabled={apiLoading || deleteLoadingId === user.id}
                                                        >
                                                            {deleteLoadingId === user.id
                                                                ? <span className="spinner-border spinner-border-sm"></span>
                                                                : <Icon icon="lucide:user-round-x" className="menu-icon" />
                                                            }
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="menu-btn bg-neutral-200 bg-hover-neutral-300 text-secondary-light fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            data-user-id={user.id}
                                                            disabled={apiLoading}
                                                        >
                                                            <Icon icon="ph:dots-three-vertical-bold" className="menu-icon" />
                                                        </button>
                                                        <div className="tooltip-menu position-absolute" style={{ display: 'none', top: '100%', right: 0 }}>
                                                            <div className="tooltip-item" onClick={() => handleEmailCredentials(user.email, user.password)}>Email Credentials</div>
                                                            <div className="tooltip-item" onClick={() => handleShowCredentials(user.id)}>Show Credentials</div>
                                                        </div>
                                                    </div>

                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                    {/* Pagination */}
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 p-16">
                                <span>
                                    Showing {Math.min((currentPage - 1) * showEntries + 1, users.length)} to {Math.min(currentPage * showEntries, users.length)} of {users.length} entries
                                </span>
                                <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                                    <li className="page-item">
                                        <button className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md" onClick={handlePreviousPage} disabled={currentPage === 1 || apiLoading}>
                                            <Icon icon="ep:d-arrow-left" />
                                        </button>
                                    </li>
                                    <li className="page-item">
                                        <button className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === 1 ? 'bg-primary-600 text-white' : ''}`} onClick={() => handlePageChange(1)} disabled={apiLoading}>1</button>
                                    </li>
                                    {users.length > showEntries && (
                                        <li className="page-item">
                                            <button className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === 2 ? 'bg-primary-600 text-white' : ''}`} onClick={() => handlePageChange(2)} disabled={apiLoading}>2</button>
                                        </li>
                                    )}
                                    <li className="page-item">
                                        <button className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md" onClick={handleNextPage} disabled={currentPage * showEntries >= users.length || apiLoading}>
                                            <Icon icon="ep:d-arrow-right" />
                                        </button>
                                    </li>
                                </ul>
                            </div>
                
            </div>

            <AddAdminUser
                isOpen={isDrawerOpen}
                onClose={() => { setUserToEdit(null); setIsDrawerOpen(false); }}
                onSubmit={handleSubmit}
                userToEdit={userToEdit}
                isLoading={apiLoading}
            />
        </>
    );
};

export default AdminUsersList;